import React, { useEffect, Suspense, lazy, useState } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { auth ,db } from "./lib/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import Protected from "./auth/Protected";
import NotFound from "./NotFound";
import LoadingSkeleton from "./LoadingSkeleton/LoadingSkeleton";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);
  return null;
};

// Lazy-loaded components
const Home = lazy(() => import("./home/home"));
const ForgotPassword = lazy(() => import("./auth/forgot-password"));
const SignIn = lazy(() => import("./auth/sign-in"));
const SignUp = lazy(() => import("./auth/sign-up"));
const SHome = lazy(() => import("./student/Student-Home"));
const MHome = lazy(() => import("./mentor/MHome"));
const MUserBookingList = lazy(() => import("./mentor/UserBookingList"));
const MUserBookingCount = lazy(() => import("./mentor/UserBookingCount"));
const MFood = lazy(() => import("./mentor/Food"));
const MProfile = lazy(() => import("./mentor/profile"));
const MSetting = lazy(() => import("./mentor/Setting"));
const MUserBookingHistory = lazy(() => import("./mentor/UserBookingHistory"));
const AProfile = lazy(() => import("./admin/Profile"));
const SProfile = lazy(() => import("./student/profile"));
const AHome = lazy(() => import("./admin/AHome"));
const APay = lazy(() => import("./admin/pay"));
const AFood = lazy(() => import("./admin/food"));
const CreateMenu = lazy(() => import("./admin/createMenu"));
const BookFood = lazy(() => import("./admin/BookFood"));
const UserResponse = lazy(() => import("./admin/userResponse"));
const ANotification = lazy(() => import("./admin/notification"));
const AUser = lazy(() => import("./admin/user"));
const ASetting = lazy(() => import("./admin/setting"));
const ManualPayment = lazy(() => import("./admin/ManualPayment"));
const ManualPaymentHistory = lazy(() => import("./admin/PaymentHistory"));
const SPaymentHistory = lazy(() => import("./student/paymentHistory"));
const SOnlinePayment = lazy(() => import("./student/onlinePayment"));
const OnlinePayment = lazy(() => import("./admin/OnlinePayment"));
const ARules = lazy(() => import("./admin/Addrules"));
const SPay = lazy(() => import("./student/payment"));
const SFood = lazy(() => import("./student/food"));
const SSetting = lazy(() => import("./student/setting"));
const FoodMenu = lazy(() => import("./student/FoodMenu"));
const TakeFood = lazy(() => import("./student/TakeFood"));

const BookingHistory = lazy(() => import("./student/BookingHistory"));
const BookingCount = lazy(() => import("./admin/BookingCount"));
const MFoodMenu = lazy(() => import("./mentor/FoodMenu"));
const MNotifications = lazy(() => import("./mentor/Notifications"));
const SNotifications = lazy(() => import("./student/Notifications"));
const SRules = lazy(() => import("./student/Rules"));
const ANotificationHistory = lazy(() => import("./admin/NotificationHistory"));
const AdminNotification = lazy(() => import("./admin/AdminNotification"));
const SOPaymentHistory = lazy(() => import("./student/PayNowHistory"));
const CancellationandRefund = lazy(() => import("./policy/CancellationandRefund"));
const ContactUs = lazy(() => import("./policy/ContactUs"));
const PrivacyPolicy = lazy(() => import("./policy/PrivacyPolicy"));
const Privacy = lazy(() => import("./policy/Privacy"));
const ShippingandDelivery = lazy(() => import("./policy/ShippingandDelivery"));
const TermsandConditions = lazy(() => import("./policy/TermsandConditions"));
const QRCode = lazy(() => import("./policy/Qrcode"));
const QuickBook = lazy(() => import("./student/QuickBook"));
const BulkDelete = lazy(() => import("./admin/BulkDelete"));
const DuplicateBookings =lazy(() => import("./admin/DulpicateBooking"))

const App = () => {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        try {
          // Get additional user data from Firestore
          const userRef = doc(db, "users", authUser.uid);
          const userSnapshot = await getDoc(userRef);
          
          if (userSnapshot.exists()) {
            const userData = userSnapshot.data();
            const combinedUser = { 
              uid: authUser.uid,
              email: authUser.email,
              emailVerified: authUser.emailVerified,
              ...userData 
            };
            setUser(combinedUser);
            localStorage.setItem("user", JSON.stringify(combinedUser));
          } else {
            setUser(authUser);
            localStorage.setItem("user", JSON.stringify(authUser));
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setUser(authUser);
          localStorage.setItem("user", JSON.stringify(authUser));
        }
      } else {
        setUser(null);
        localStorage.removeItem("user");
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <LoadingSkeleton />;
  }

  const userRole = user?.isAdmin ? "Admin" : user?.isMentor ? "Mentor" : "Student";

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<LoadingSkeleton />}>
        <Routes>
          <Route
            path="/"
            element={user ? <Navigate to={`/${userRole.toLowerCase()}/home`} replace /> : <Home />}
          />
          <Route
            path="/auth/sign-up"
            element={user ? <Navigate to={`/${userRole.toLowerCase()}/home`} replace /> : <SignUp />}
          />
          <Route
            path="/auth/sign-in"
            element={user ? <Navigate to={`/${userRole.toLowerCase()}/home`} replace /> : <SignIn />}
          />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />

          {/* Admin Routes */}
          <Route element={<Protected allowedRoles={["Admin"]} />}>
            <Route path="/admin/home" element={<AHome />} />
            <Route path="/admin/payment" element={<APay />} />
            <Route path="/admin/notificationControl" element={<ANotification />} />
            <Route path="/admin/foodControl" element={<AFood />} />
            <Route path="/admin/userControl" element={<AUser />} />
            <Route path="/admin/settings" element={<ASetting />} />
            <Route path="/admin/online-payments" element={<OnlinePayment />} />
            <Route path="/admin/manual-payments" element={<ManualPayment />} />
            <Route path="/admin/manual-payments/history" element={<ManualPaymentHistory />} />
            <Route path="/admin/profile" element={<AProfile />} />
            <Route path="/admin/food/create-menu" element={<CreateMenu />} />
            <Route path="/admin/food/user-response" element={<UserResponse />} />
            <Route path="/admin/food/book-food" element={<BookFood />} />
            <Route path="/admin/food/user-response/booking-count" element={<BookingCount />} />
            <Route path="/admin/notification-history" element={<ANotificationHistory />} />
            <Route path="/admin/notifications" element={<AdminNotification />} />
            <Route path="/admin/add-rules" element={<ARules />} />
            <Route path="/admin/booking-delete" element={<BulkDelete />} />
             <Route path="/admin/Duplicate-bookings" element={<DuplicateBookings/>} />
          </Route>

          {/* Student Routes */}
          <Route element={<Protected allowedRoles={["Student"]} />}>
            <Route path="/student/home" element={<SHome />} />
            <Route path="/student/profile" element={<SProfile />} />
            <Route path="/student/payment" element={<SPay />} />
            <Route path="/student/setting" element={<SPay />} />
            <Route path="/student/food" element={<SFood />} />
            <Route path="/student/settings" element={<SSetting />} />
            <Route path="/student/food/food-menu" element={<FoodMenu />} />
            <Route path="/student/food/booking-history" element={<BookingHistory />} />
            <Route path="/student/food/take-food" element={<TakeFood />} />
            <Route path="/student/payment/pay-now" element={<SOnlinePayment />} />
            <Route path="/student/payment/history" element={<SPaymentHistory />} />
            <Route path="/student/notifications" element={<SNotifications />} />
            <Route path="/student/online-payment-history" element={<SOPaymentHistory />} />
            <Route path="/student/rules" element={<SRules />} />
            <Route path="/student/food/quick-book" element={<QuickBook />} />
          </Route>

          {/* Mentor Routes */}
          <Route element={<Protected allowedRoles={["Mentor"]} />}>
            <Route path="/mentor/home" element={<MHome />} />
            <Route path="/mentor/food" element={<MFood />} />
            <Route path="/mentor/notifications" element={<MNotifications />} />
            <Route path="/mentor/profile" element={<MProfile />} />
            <Route path="/mentor/food/user-booking-list" element={<MUserBookingList />} />
            <Route path="/mentor/food/user-booking-count" element={<MUserBookingCount />} />
            <Route path="/mentor/food/user-booking-history" element={<MUserBookingHistory />} />
            <Route path="/mentor/setting" element={<MSetting />} />
            <Route path="/mentor/food/menu" element={<MFoodMenu />} />
          </Route>

          {/* Policy Routes */}
          <Route path="/not-found" element={<NotFound />} />
          <Route path="/Cancellation-and-Refund" element={<CancellationandRefund />} />
          <Route path="/Contact-Us" element={<ContactUs />} />
          <Route path="/Privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/Privacy" element={<Privacy />} />
          <Route path="/Shipping-and-delivery" element={<ShippingandDelivery />} />
          <Route path="/Terms-and-conditions" element={<TermsandConditions />} />
          <Route path="/qrcode" element={<QRCode />} />

          {/* Catch-all route */}
          <Route path="*" element={<Navigate to="/not-found" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;